import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateTccActionButton } from "routes/tcc/components/create-tcc-action-buttons";
import useAxios from "utils/useAxios";
import { ListingModalRef } from "../../../../components/ButtonModal";
import SubmitConfirmationModal from "./submitConfirmationModal";
import RejectConfirmationModal from "./rejectConfirmationModal";
import ApproveConfirmationModal from "./approveConfirmationModal";
import Spinner from "../../../../components/Spinner";
import { useCreatePrintingTechniqueStore } from "../../context/printingTechnique/create/CreatePrintingTechniqueContext";
import { submitPrintingTechniqueRequest } from "../../helpers/artworkPrintingTechniqueSubmitHelper";
import { Predicates } from "../../../../libraries/predicates/predicates";
import { PrintingTechniqueActions } from "../../state/printingTechnique/create";
import { CreatePrintingTechniqueTypes } from "../../state/printingTechnique/create/data";
import { DeleteConfirmationModal } from "./deleteConfirmationModal";

export const actionButtons = () => {};

type ArtworkPrintingTechniqueActionButtonsGroup = {
  isReadOnlyRequest: boolean;
  isEditing: boolean;
  isSaveOrSubmitInProgress: boolean;
  username: string;
  hasApproverPermission: boolean;
  setIsSaveOrSubmitInProgress: (isLoading: boolean) => void;
  hasCreatorPermission: any;
};

export const ArtworkPrintingTechniqueActionButtonsGroup = ({
  isReadOnlyRequest,
  isEditing,
  isSaveOrSubmitInProgress,
  username,
  setIsSaveOrSubmitInProgress,
  hasApproverPermission,
  hasCreatorPermission,
}: ArtworkPrintingTechniqueActionButtonsGroup) => {
  const navigate = useNavigate();
  const axios = useAxios();
  const modalRef = useRef<ListingModalRef>(null);

  const { state, dispatch } = useCreatePrintingTechniqueStore();

  const [previeousState, setPreviousState] = useState<any>({});

  const actionButtons: { [key: string]: any } = {
    save: {
      show:
        !state.isReadOnly &&
        hasCreatorPermission &&
        (state.status?.name === "CREATED" ||
          (state.status?.name !== "SUBMITTED" &&
            state.status?.name !== "APPROVED")),
      disabled: isSaveOrSubmitInProgress || !state.isValid,
      buttonStyleClass: "btn-primary",
      description: "Save",
      className: "ms-auto",
      clickHandler: () => {
        const data: any = {
          requestId: state?.id,
          action: state?.id ? "EDITED" : "CREATED",
          action_user: username,
          data: JSON.stringify(state),
          reason: null,
        };
        submitPrintingTechniqueRequest(
          axios,
          data,
          username,
          dispatch,
          setIsSaveOrSubmitInProgress,
          navigate,
          "/artwork/approvals",
          true,
          state.image,
        );
      },
      isButtonModal: false,
    },
    submit: {
      show:
        !hasApproverPermission &&
        ((!state.isReadOnly &&
          hasCreatorPermission &&
          state.status?.name !== "SUBMITTED") ||
          state.status?.name === "CREATED"),
      disabled: isSaveOrSubmitInProgress || !state.isValid,
      buttonStyleClass: "btn-primary",
      description: "Submit for Approval",
      className: "ms-auto",
      clickHandler: () => {},
      isButtonModal: true,
      modalTitle: "Confirm Artwork Request submission",
      modal: (
        <SubmitConfirmationModal
          onConfirm={() => {
            const data: any = {
              requestId: state?.id,
              action: "SUBMITTED",
              action_user: username,
              data: JSON.stringify(state),
              reason: null,
              comment: state.submit_comment,
            };

            submitPrintingTechniqueRequest(
              axios,
              data,
              username,
              dispatch,
              setIsSaveOrSubmitInProgress,
              navigate,
              "/artwork/approvals",
              true,
              state.image,
            );
            //
            modalRef.current?.closeModal();
          }}
          parentModalRef={modalRef}
        />
      ),
      modalRef: modalRef,
    },
    approve: {
      show:
        state.status?.name !== "APPROVED" &&
        state.status?.name !== "REJECTED" &&
        hasApproverPermission,
      disabled: isSaveOrSubmitInProgress || !state.isValid,
      buttonStyleClass: "btn-primary",
      description: "Approve",
      className: "ms-auto",
      clickHandler: () => {},
      isButtonModal: true,
      modalTitle: "Approve",
      modal: (
        <ApproveConfirmationModal
          onConfirm={() => {
            const data: any = {
              requestId: state?.id,
              action: "APPROVED",
              action_user: username,
              data: JSON.stringify(state),
              reason:
                Predicates.isNotNullAndNotUndefinedAndNotEmpty(
                  state?.reject_reason,
                ) && state?.reject_reason,
              comment: state.submit_comment,
            };

            submitPrintingTechniqueRequest(
              axios,
              data,
              username,
              dispatch,
              setIsSaveOrSubmitInProgress,
              navigate,
              "/artwork/",
              true,
              state.image,
            );

            modalRef.current?.closeModal();
          }}
          parentModalRef={modalRef}
        />
      ),
      modalRef: modalRef,
    },
    reject: {
      show:
        state.status?.name !== "APPROVED" &&
        state.status?.name !== "REJECTED" &&
        state.status?.name !== "CREATED" &&
        !!state.status?.name &&
        hasApproverPermission,
      disabled: isSaveOrSubmitInProgress || !state.isValid,
      buttonStyleClass: "btn-danger",
      description: "Reject",
      className: "ms-auto",
      clickHandler: () => {},
      isButtonModal: true,
      modalTitle: "Confirm Artwork Request Rejection",
      modal: (
        <RejectConfirmationModal
          onConfirm={() => {
            const data: any = {
              requestId: state?.id,
              action: "REJECTED",
              action_user: username,
              data: JSON.stringify(state),
              reason:
                Predicates.isNotNullAndNotUndefinedAndNotEmpty(
                  state?.reject_reason,
                ) && state?.reject_reason,
              comment: state.submit_comment,
            };

            submitPrintingTechniqueRequest(
              axios,
              data,
              username,
              dispatch,
              setIsSaveOrSubmitInProgress,
              navigate,
              "/artwork/",
              true,
              state.image,
            );

            modalRef.current?.closeModal();
          }}
          parentModalRef={modalRef}
        />
      ),
      modalRef: modalRef,
    },

    edit: {
      show:
        state.status?.name === "APPROVED" &&
        hasApproverPermission &&
        // !isEditing &&
        state.isReadOnly,
      disabled: isSaveOrSubmitInProgress,
      buttonStyleClass: "btn-primary",
      description: "Edit",
      className: "ms-auto",
      clickHandler: () => {
        console.log("saving the changes");
        setPreviousState({ ...state });
        dispatch(
          PrintingTechniqueActions.setStateValue(
            CreatePrintingTechniqueTypes.SET_IS_READ_ONLY,
            false,
          ),
        );
      },
      modalTitle: "Approve",
      isButtonModal: false,
    },
    cancel: {
      show:
        state.status?.name === "APPROVED" &&
        hasApproverPermission &&
        !state.isReadOnly,
      disabled: isSaveOrSubmitInProgress,
      buttonStyleClass: "btn-secondary",
      description: "Cancel",
      className: "ms-auto",
      clickHandler: () => {
        dispatch(
          PrintingTechniqueActions.setStateValue(
            CreatePrintingTechniqueTypes.SET_IS_READ_ONLY,
            true,
          ),
        );
        if (!!previeousState.id) {
          dispatch(
            PrintingTechniqueActions.setStateValue(
              CreatePrintingTechniqueTypes.SET_PRINTING_TECHNIQUE,
              {
                ...previeousState,
              },
            ),
          );
        }
      },
      modalTitle: "Approve",
      isButtonModal: false,
    },
    approveEdit: {
      show:
        !isReadOnlyRequest &&
          state?.status?.name === "APPROVED" &&
          // isEditing &&
          hasApproverPermission,
      disabled: isSaveOrSubmitInProgress || !state.isValid,
      buttonStyleClass: "btn-primary",
      description: "Approve edit",
      className: "ms-auto",
      clickHandler: () => {
        console.log("approving the changes");
      },
      isButtonModal: true,
      modalTitle: "Approve edit",
      modal: (
      <ApproveConfirmationModal
         onConfirm={() => {
            const data: any = {
              requestId: state?.id,
              action: "EDITED",
              action_user: username,
              data: JSON.stringify(state),
              reason:
                Predicates.isNotNullAndNotUndefinedAndNotEmpty(
                  state?.reject_reason,
                ) && state?.reject_reason,
              comment: state.submit_comment,
            };

            submitPrintingTechniqueRequest(
              axios,
              data,
              username,
              dispatch,
              setIsSaveOrSubmitInProgress,
              navigate,
              "/artwork/",
              true,
              state.image,
            );

            modalRef.current?.closeModal();
          }}
          parentModalRef={modalRef}
        />
      ),
      modalRef: modalRef,
    },
    delete: {
      show: false, //for now is impossible to delete artworks, verify if this feature should be turned on and test if the request is working
      // state.status?.name === "APPROVED" &&
      // hasApproverPermission &&
      // state.isReadOnly,
      disabled: isSaveOrSubmitInProgress,
      buttonStyleClass: "btn-danger",
      description: "Delete",
      className: "ms-auto",
      clickHandler: () => {},
      isButtonModal: true,
      modalTitle: "Confirm Delete",
      modal: (
        <DeleteConfirmationModal
          onConfirm={() => {
            const data: any = {
              requestId: state?.id,
              action: "DELETED",
              action_user: username,
              context: 45,
              country: 21,
              data: JSON.stringify(state),
              reason: null,
              comment: state.submit_comment,
            };

            submitPrintingTechniqueRequest(
              axios,
              data,
              username,
              dispatch,
              setIsSaveOrSubmitInProgress,
              navigate,
              "/artwork-master/search",
              true,
            );

            modalRef.current?.closeModal();
          }}
          parentModalRef={modalRef}
        />
      ),
      modalRef: modalRef,
    },
  };

  return (
    <div className="d-flex justify-content-end mt-auto pt-3">
      <div className="d-flex gap-3 me-auto">
        <CreateTccActionButton {...actionButtons.save} />
        <CreateTccActionButton {...actionButtons.reject} />
        <CreateTccActionButton {...actionButtons.delete} />
      </div>

      <div className="d-flex gap-3" style={{ height: 38 }}>
        {isSaveOrSubmitInProgress && (
          <Spinner hideLabel={true} mediumSpinner={true} />
        )}

        <CreateTccActionButton {...actionButtons.submit} />

        <CreateTccActionButton {...actionButtons.approve} />

        <CreateTccActionButton {...actionButtons.continueReview} />

        <CreateTccActionButton {...actionButtons.reOpen} />
        <CreateTccActionButton {...actionButtons.approveEdit} />

        <CreateTccActionButton {...actionButtons.edit} />

        <CreateTccActionButton {...actionButtons.cancel} />
      </div>
    </div>
  );
};
